import type Phaser from 'phaser';
import { AudioSettingsService } from '../../services/audioSettingsService';
import { sceneTrackFetcher, type TrackFetcher } from './musicFiles';

/**
 * Music loudness as a fraction of the master volume. Sits below SFX so the
 * backing track stays in the background. Effective volume is
 * MUSIC_VOLUME × game.sound.volume; the master mute silences it too.
 */
const MUSIC_VOLUME = 0.65;

/**
 * Phaser.Sound.Events.UNLOCKED. Inlined as a literal so this module stays a
 * type-only Phaser import — pulling the engine in at runtime breaks the
 * node-environment unit tests (Phaser touches `navigator` on load).
 */
const UNLOCKED_EVENT = 'unlock';

/**
 * Owns the single looping background track. Independent of the master
 * mute/volume controls in AudioService: turning music off here stops the track
 * without affecting SFX. A track that is not loaded yet is handed to the
 * `fetchTrack` downloader and starts when it arrives — unless anything was
 * played or stopped in the meantime. With no downloader, or a key it does not
 * know (pre-asset-gen / headless), the game simply runs without music.
 */
export class MusicService {
  private current?: Phaser.Sound.BaseSound;
  private currentKey?: string;
  /** Bumped by every play/stop, so a download that lands late can tell it is stale. */
  private generation = 0;

  constructor(
    private readonly game: Phaser.Game,
    private readonly settings: AudioSettingsService,
    private readonly fetchTrack?: TrackFetcher,
  ) {}

  /** Remember + play the given track on loop. Stops any current track first. */
  playTrack(key: string): void {
    this.currentKey = key;
    this.stop();
    if (!this.settings.get().musicEnabled) return;
    if (this.game.cache.audio.exists(key)) {
      this.start(key);
      return;
    }
    const asked = this.generation;
    this.fetchTrack?.(key, () => {
      if (this.generation === asked) this.start(key);
    });
  }

  private start(key: string): void {
    const sound = this.game.sound.add(key, { loop: true, volume: MUSIC_VOLUME });
    this.current = sound;
    if (this.game.sound.locked) {
      // Autoplay is blocked until the first user gesture; start it then.
      this.game.sound.once(UNLOCKED_EVENT, () => sound.play());
    } else {
      sound.play();
    }
  }

  /** Stop + tear down the current track. Keeps the remembered key. */
  stop(): void {
    this.generation++;
    if (!this.current) return;
    this.current.stop();
    this.current.destroy();
    this.current = undefined;
  }

  /** Persist the toggle; stop when off, resume the remembered track when on. */
  setEnabled(on: boolean): void {
    this.settings.setMusicEnabled(on);
    if (!on) {
      this.stop();
    } else if (this.currentKey) {
      this.playTrack(this.currentKey);
    }
  }
}

/** One shared MusicService per game, stored on the registry. */
export function getMusicService(scene: Phaser.Scene): MusicService {
  const reg = scene.game.registry;
  let svc = reg.get('musicService') as MusicService | undefined;
  if (!svc) {
    svc = new MusicService(
      scene.game,
      new AudioSettingsService(window.localStorage),
      sceneTrackFetcher(scene.game),
    );
    reg.set('musicService', svc);
  }
  return svc;
}
